import { adotarIdentidadesDerivadas } from './adocaoDerivada.js';
import { ErroDeMapeamento, deRemoto, paraRemoto } from './mapeamento.js';
import { haLeituraMortaDoRelatorio, invalidarBaseDoRelatorio, marcarBaseDoRelatorioCompleta, tabelaPertenceABaseDoRelatorio, } from './integridadeDoRelatorio.js';
import { CONTA_LOCAL, CURSOR_ZERO, JANELA_DE_FALHA_MS, LOTE_DO_PUSH, PAGINA_DO_PULL, SOBREPOSICAO_DO_PULL_MS, TABELAS, TETO_DE_TENTATIVAS, dedupFila, deveAplicarRemota, emLotes, normalizarIso, } from './protocolo.js';
import { aoMutarLocalmente } from './sinalDeMutacao.js';
export const INTERVALOS_MIN = [15, 30, 60, 240];
/** A maior espera que os servidores pediram neste ciclo, ou `null` se
 *  nenhum pediu. A maior, e não a primeira: esperar menos do que a tabela
 *  mais estrangulada pediu é voltar cedo demais para ela. */
function esperaPedida(falhas) {
    const pedidos = falhas
        .map((f) => f.esperarMs)
        .filter((ms) => ms !== undefined);
    return pedidos.length === 0 ? null : Math.max(...pedidos);
}
const ESPERA_DO_EVENTO = 500;
const ESPERA_DA_MUTACAO = 2000;
const TETO_DO_BACKOFF = 5 * 60 * 1000;
export class MotorDeSincronizacao {
    #deps;
    #estado = {
        fase: 'desligado',
        realtime: 'desligado',
        ultimaSincronizacaoEm: null,
        pendentes: 0,
        mortas: 0,
        rejeitadasNoUltimoCiclo: 0,
        erros: [],
        erro: null,
    };
    #ouvintes = new Set();
    #cicloEmCurso = null;
    #agenda = null;
    #novaTentativa = null;
    #backoffMs = 1000;
    #desligarRealtime = null;
    #eventoAgendado = null;
    #mutacaoAgendada = null;
    #cancelarOuvintes = [];
    #iniciado = false;
    /** `true` enquanto `parar()`/`#configurar` desligam o canal de propósito:
     *  o `CLOSED` que isso dispara não é falha, é o desligamento pedido. */
    #desligandoRealtime = false;
    /** Dono do ciclo em curso, para carimbar `dead_letter.user_id`. */
    #usuarioDoCiclo = null;
    /** Última sessão anunciada por `aoMudarSessao`, para não repetir ciclo. */
    #usuarioDaSessao = null;
    constructor(deps) {
        this.#deps = deps;
    }
    estado() {
        return this.#estado;
    }
    assinar(ouvinte) {
        this.#ouvintes.add(ouvinte);
        ouvinte(this.#estado);
        return () => {
            this.#ouvintes.delete(ouvinte);
        };
    }
    #mudar(parcial) {
        this.#estado = {
            ...this.#estado,
            ...parcial,
            erro: parcial.erros
                ? parcial.erros.map((f) => f.mensagem).join(' · ') || null
                : this.#estado.erro,
        };
        for (const o of this.#ouvintes)
            o(this.#estado);
    }
    #agora() {
        return this.#deps.agora?.() ?? new Date().toISOString();
    }
    async #valor(chave) {
        return (await this.#deps.banco.estado.get(chave))?.valor ?? null;
    }
    async #gravarValor(chave, valor) {
        await this.#deps.banco.estado.put({ chave, valor });
    }
    async getModo() {
        const m = await this.#valor('modo');
        return m === 'manual' || m === 'aoAbrir' || m === 'periodico'
            ? m
            : 'continuo';
    }
    async setModo(modo) {
        await this.#gravarValor('modo', modo);
        this.#configurar(await this.getIntervaloMin(), modo);
        if (modo !== 'manual')
            void this.sincronizarAgora();
    }
    async getIntervaloMin() {
        const n = Number(await this.#valor('intervaloMin'));
        return INTERVALOS_MIN.includes(n) ? n : 30;
    }
    async setIntervaloMin(minutos) {
        await this.#gravarValor('intervaloMin', String(minutos));
        this.#configurar(minutos, await this.getModo());
    }
    /** Registra gatilhos e arma o modo em vigor. Chamar duas vezes não
     *  registra nada de novo: o React em modo estrito monta o efeito duas
     *  vezes, e um ouvinte duplicado sincronizaria em dobro para sempre. */
    async iniciar() {
        if (this.#iniciado)
            return;
        this.#iniciado = true;
        await this.#atualizarContadores();
        this.#cancelarOuvintes.push(aoMutarLocalmente(() => {
            if (this.#mutacaoAgendada !== null)
                clearTimeout(this.#mutacaoAgendada);
            this.#mutacaoAgendada = setTimeout(() => {
                this.#mutacaoAgendada = null;
                void (async () => {
                    if ((await this.getModo()) === 'continuo')
                        void this.sincronizarAgora();
                    else
                        await this.#atualizarContadores();
                })();
            }, ESPERA_DA_MUTACAO);
        }));
        if (typeof document !== 'undefined') {
            const aoVoltar = () => {
                if (document.visibilityState !== 'visible')
                    return;
                void (async () => {
                    if ((await this.getModo()) !== 'manual')
                        void this.sincronizarAgora();
                })();
            };
            document.addEventListener('visibilitychange', aoVoltar);
            this.#cancelarOuvintes.push(() => document.removeEventListener('visibilitychange', aoVoltar));
        }
        if (typeof window !== 'undefined') {
            const aoConectar = () => void this.aoReconectar();
            window.addEventListener('online', aoConectar);
            this.#cancelarOuvintes.push(() => window.removeEventListener('online', aoConectar));
        }
        const modo = await this.getModo();
        this.#configurar(await this.getIntervaloMin(), modo);
        if (modo !== 'manual')
            void this.sincronizarAgora();
    }
    parar() {
        this.#iniciado = false;
        // Esquecer a sessão anunciada: quem reiniciar o motor precisa que o
        // primeiro `aoMudarSessao` volte a valer, mesmo sendo a mesma conta.
        this.#usuarioDaSessao = null;
        for (const c of this.#cancelarOuvintes)
            c();
        this.#cancelarOuvintes = [];
        this.#configurar(0, 'manual');
        this.#mudar({ fase: 'desligado', realtime: 'desligado' });
        if (this.#novaTentativa !== null) {
            clearTimeout(this.#novaTentativa);
            this.#novaTentativa = null;
        }
        if (this.#eventoAgendado !== null) {
            clearTimeout(this.#eventoAgendado);
            this.#eventoAgendado = null;
        }
        if (this.#mutacaoAgendada !== null) {
            clearTimeout(this.#mutacaoAgendada);
            this.#mutacaoAgendada = null;
        }
    }
    #online() {
        if (this.#deps.online)
            return this.#deps.online();
        // Só o `false` explícito conta como sem rede: no Node dos testes existe
        // um `navigator` sem `onLine`, e presumir "offline" ali esconderia
        // falhas de canal que devem aparecer.
        return globalThis.navigator?.onLine !== false;
    }
    #donoDoCiclo() {
        if (this.#usuarioDoCiclo === null) {
            throw new Error('Ciclo sem usuário: linha morta sem dono.');
        }
        return this.#usuarioDoCiclo;
    }
    #configurar(minutos, modo) {
        if (this.#agenda !== null)
            clearInterval(this.#agenda);
        this.#agenda = null;
        if ((modo === 'periodico' || modo === 'continuo') && minutos > 0) {
            this.#agenda = setInterval(() => void this.sincronizarAgora(), minutos * 60_000);
        }
        if (modo === 'continuo' &&
            this.#deps.realtime &&
            this.#desligarRealtime === null) {
            this.#desligarRealtime = this.#deps.realtime(() => {
                if (this.#eventoAgendado !== null)
                    clearTimeout(this.#eventoAgendado);
                this.#eventoAgendado = setTimeout(() => {
                    this.#eventoAgendado = null;
                    void this.sincronizarAgora();
                }, ESPERA_DO_EVENTO);
            }, (estado, erro) => {
                this.#aoEstadoDoRealtime(estado, erro);
            });
        }
        else if (modo !== 'continuo' && this.#desligarRealtime !== null) {
            // O `unsubscribe()` chamado a seguir pode reportar `CLOSED` de forma
            // síncrona ou (mais comum) assíncrona; a flag cobre os dois casos.
            this.#desligandoRealtime = true;
            try {
                this.#desligarRealtime();
            }
            catch (erro) {
                // Engolir erro do disposer; reportar via `erros` para visibilidade.
                this.#mudar({
                    realtime: 'erro',
                    erros: [
                        ...this.#estado.erros.filter((f) => f.tabela !== 'realtime'),
                        {
                            tabela: 'realtime',
                            mensagem: `Erro ao desligar canal: ${erro instanceof Error ? erro.message : String(erro)}`,
                            transitoria: false,
                        },
                    ],
                });
            }
            finally {
                this.#desligarRealtime = null;
                this.#mudar({ realtime: 'desligado' });
                this.#desligandoRealtime = false;
            }
        }
    }
    /**
     * Estados do canal, como o supabase-js os reporta. Um canal caído não é
     * falha de sincronização (o intervalo e os outros gatilhos seguem), mas
     * precisa aparecer: sem ele o app deixa de ser ao vivo em silêncio.
     */
    #aoEstadoDoRealtime(estado, erro) {
        // Desligamento de propósito (`parar()`, ou a troca para um modo que não
        // é contínuo) manda em tudo, inclusive num `SUBSCRIBED` atrasado: sem
        // esta guarda no topo, ele deixaria `realtime: 'ligado'` depois de o
        // `finally` já ter gravado `'desligado'`.
        if (this.#desligandoRealtime)
            return;
        if (estado === 'SUBSCRIBED') {
            // Vale também depois de uma queda por falta de rede: o cliente
            // Realtime reconecta sozinho, e o estado volta a "ligado".
            this.#mudar({
                realtime: 'ligado',
                erros: this.#estado.erros.filter((f) => f.tabela !== 'realtime'),
            });
            return;
        }
        // Um `CLOSED` supérfluo depois de já estar desligado não é falha (o
        // dublê de teste chama a limpeza mais de uma vez).
        if (estado === 'CLOSED' && this.#estado.realtime === 'desligado')
            return;
        if (estado === 'CHANNEL_ERROR' ||
            estado === 'TIMED_OUT' ||
            estado === 'CLOSED') {
            // Sem rede o canal cai sempre; isso é a rede, não uma falha do app.
            // O estado fica "desligado" e a lista de erros, limpa — `aoReconectar`
            // dispara o ciclo quando a rede voltar e o canal rearma sozinho.
            if (!this.#online()) {
                this.#mudar({
                    realtime: 'desligado',
                    erros: this.#estado.erros.filter((f) => f.tabela !== 'realtime'),
                });
                return;
            }
            this.#mudar({
                realtime: 'erro',
                erros: [
                    ...this.#estado.erros.filter((f) => f.tabela !== 'realtime'),
                    {
                        tabela: 'realtime',
                        mensagem: `Canal ao vivo: ${estado}${erro ? ` (${erro.message})` : ''}`,
                        transitoria: true,
                    },
                ],
            });
        }
    }
    /**
     * A sessão mudou (entrou, saiu, trocou de conta). Sem este gatilho o motor
     * só descobre a nova sessão no próximo evento alheio (`visibilitychange`,
     * `online`, mutação local ou o intervalo), e Ajustes segue dizendo "sem
     * conta" depois de o usuário entrar. Quem chama é o app, num único lugar,
     * a partir de `onAuthStateChange`.
     *
     * Comparar com o usuário já conhecido evita ciclo à toa a cada
     * `TOKEN_REFRESHED` da mesma conta; um `TOKEN_REFRESHED` que traz conta
     * onde não havia nenhuma continua disparando, porque o id mudou.
     */
    async aoMudarSessao(usuario) {
        if (usuario === this.#usuarioDaSessao)
            return;
        this.#usuarioDaSessao = usuario;
        if (usuario === null) {
            // Sem sessão não há o que sincronizar, e o canal ao vivo foi (ou está
            // sendo) desligado por quem observa a sessão: o estado precisa dizer
            // isso já, sem esperar um ciclo.
            this.#mudar({ fase: 'desligado', realtime: 'desligado', erros: [] });
            return;
        }
        // Conta nova: o canal da anterior caiu (ou está caindo) e o novo ainda
        // não assinou; dizer "ao vivo" nesse intervalo enganaria os Ajustes.
        // Quem rearma o canal restaura o estado ao `SUBSCRIBED`.
        if (this.#estado.realtime === 'ligado')
            this.#mudar({ realtime: 'desligado' });
        // Só depois de toda a parte síncrona: o primeiro `await` daqui devolve o
        // controle, e um `SUBSCRIBED` que chegasse nesse intervalo seria
        // rebaixado a `desligado` pela linha acima ao voltarmos.
        await this.#adotarAoEntrar(usuario);
        if ((await this.getModo()) === 'manual')
            return;
        await this.sincronizarAgora();
    }
    /**
     * A conta que entra adota o que foi escrito sem conta — e adota **na hora**,
     * sem esperar ciclo nenhum. Vale para a primeira conta a aparecer neste
     * aparelho e para o **relogin da mesma conta**; a conta *diferente* é caso
     * do ciclo, que purga antes de adotar.
     *
     * Por que isto existe, com todas as letras: `estado['usuario']` só era
     * escrito **dentro** de um ciclo bem-sucedido, e a purga de troca de conta
     * só corre quando há dono anterior. Entrar como `u1` sem rede (ou em modo
     * manual) não gravava nada; sair e entrar como `u2` encontrava
     * `anterior === null`, **não purgava**, e o `paraRemoto` carimbava `u2` em
     * tudo o que estava na fila — inclusive nas linhas que `u1` escreveu. Nomes,
     * endereços e telefones de pessoas reais subiam para a conta errada, em
     * silêncio. O mesmo buraco deixava `relatorioDeMortas` devolver lista vazia
     * antes do primeiro ciclo, mesmo havendo linhas mortas.
     *
     * **Nunca assume no lugar de outra conta.** Sobrescrever um dono
     * *diferente* aqui seria trocar um defeito por outro: a troca `u1 → u2`
     * chegaria ao ciclo com `anterior === u2` e a purga voltaria a não
     * acontecer. Quem promove `u2` a dono é o ciclo, **depois** de purgar o que
     * era de `u1`.
     *
     * **Mas o dono gravado ser o próprio usuário não é motivo para não adotar.**
     * Quem sai da conta (ou perde o refresh token no campo), registra visitas e
     * volta **na mesma conta** encontra `estado['usuario'] === u1`. Com a saída
     * antecipada de antes, as linhas nascidas `CONTA_LOCAL` nesse intervalo
     * subiam carimbadas certo — o `paraRemoto` resolve o servidor — e a cópia
     * local ficava sentinela **para sempre**: `deveAplicarRemota` compara com
     * `>` estrito, então nem o eco do pull a reescrevia. Aí a purga de uma troca
     * `u1 → u2` as poupava (elas não são de `u1`, são da `CONTA_LOCAL`), `u2` as
     * via na tela e as subia para o servidor **dela** na primeira reescrita. É a
     * mesma falha da adoção só-na-saída, com o primeiro login trocado pelo
     * relogin. Adotar aqui fecha a janela: `anterior = null`, porque não há o
     * que purgar quando a conta é a mesma.
     *
     * O primeiro login continua sem purgar, como o ADR-0005 promete: as linhas
     * escritas sem conta são adotadas por quem entrar — e adotadas de verdade,
     * também na cópia local (ver `#assumirODono`).
     */
    async #adotarAoEntrar(usuario) {
        const dono = await this.#valor('usuario');
        if (dono !== null && dono !== usuario) {
            // No modo manual não haverá ciclo para purgar e zerar cursores agora.
            // Um marcador guardado da última passagem desta conta descreveria
            // outra cópia local; invalidá-lo já impede uma apuração vazia de se
            // apresentar como completa enquanto o usuário continua offline.
            await invalidarBaseDoRelatorio(this.#deps.banco, usuario);
            return;
        }
        await this.#assumirODono(usuario, null);
    }
    /**
     * `dono` passa a ser o dono deste aparelho. Numa transação só, porque as
     * três coisas que isto faz não podem existir pela metade.
     *
     * **A regra, inteira: adota-se o que foi escrito sem conta e apaga-se o que
     * é de qualquer outra conta.** Depois daqui, toda linha do Dexie é de `dono`
     * ou da `CONTA_LOCAL` — não sobra linha estrangeira.
     *
     * **1. Adoção — e ela reescreve a cópia local.** As linhas da `CONTA_LOCAL`
     * passam a ter `dono` como `user_id` no Dexie. Antes, a adoção existia só na
     * saída (`paraRemoto` carimbava o dono do ciclo) e a linha ficava sentinela
     * no banco local para sempre: `deveAplicarRemota` compara com `>` estrito,
     * então nem o eco do pull a reescrevia. `CONTA_LOCAL` passava a significar
     * "nunca reescrita desde que nasceu sem conta", e não "ainda não adotada" —
     * e aí uma linha que `u1` já tinha adotado **e subido** sobrevivia à purga
     * do item 2, aparecia para `u2` e subia para o servidor de `u2` na primeira
     * reescrita (tocar na tira "Para visitar hoje" já basta). Reescrevendo aqui,
     * a sentinela volta a querer dizer o que o resto do motor supõe que ela
     * quer dizer.
     *
     * **O carimbo do LWW não se mexe.** O conteúdo da linha não mudou; mudou de
     * quem ela é. Avançar `updated_at` faria a cópia local ganhar de uma versão
     * remota legitimamente mais nova — uma edição feita noutro aparelho seria
     * descartada para registrar um fato que ninguém editou. Linhas cuja chave
     * não depende do dono também não são enfileiradas: o servidor já conhece o
     * dono carimbado na primeira saída. Perfil e mês são a exceção, porque a
     * chave derivada mudou; elas ganham uma fila canônica sem payload antigo.
     *
     * **2. Purga: sai a linha de qualquer conta que não seja `dono`** — a
     * anterior, e também a de uma terceira conta, se houver. Guardar a linha de
     * terceiro foi tentado e desfeito: ela não é de quem está com o aparelho (ao
     * contrário da `CONTA_LOCAL`, que é), o dado dessa pessoa está no servidor
     * dela, e o que sobrava aqui era um objeto sem dono possível — a fila dele
     * subia para a conta nova, uma reescrita local o publicava na conta errada
     * (`FolhaRegistrar` preserva o `user_id` gravado), e a cópia local ficava em
     * desacordo permanente com a conta que o publicou. Apagar fecha os três de
     * uma vez, e o preço é uma cópia local de dado que já está no servidor de
     * quem o escreveu.
     *
     * **A linha da conta nova não é tocada.** O critério nunca é "tudo o que
     * não é `CONTA_LOCAL`": as linhas que a própria conta que entra escreveu
     * antes do primeiro ciclo (login sem rede, modo manual — os dois existem
     * neste app) são dela, e varrê-las era apagar dados de campo em silêncio e
     * sem Desfazer.
     *
     * O que sai de fato: as linhas de outra conta nas oito tabelas de dados (sem
     * limpá-las, a conta nova abriria o app vendo os dados de outra pessoa); as
     * entradas da fila **dessas** linhas; as mortas de qualquer outra conta; e,
     * quando houve troca, os cursores, que eram da conta anterior (zerá-los faz
     * o pull seguinte trazer tudo do zero).
     *
     * **A fila sai por linha apagada, não por sobrevivente.** Descartar tudo o
     * que não sobreviveu apagaria também a entrada de uma linha que o app apagou
     * de propósito — uma lápide da própria conta, que tem de subir.
     *
     * **As mortas de outra conta saem sempre**, haja troca ou não:
     * `reenfileirarMortas()` devolve à fila **todas** as mortas de envio, sem
     * olhar dono, e a fila é carimbada com o dono do ciclo (ADR-0005). Uma morta
     * estrangeira esquecida aqui seria a mesma publicação na conta errada por
     * outro caminho. Purgadas, não movidas: mover as deixaria visíveis.
     *
     * **3. `estado['usuario']`**, o registro de quem é o dono, na mesma
     * transação. Gravar o dono com as linhas ainda em `CONTA_LOCAL` seria o
     * defeito de volta, porque `#adotarAoEntrar` já não entraria da próxima
     * vez.
     *
     * A varredura é `each` porque `user_id` só é índice em `perfil` e em
     * `dead_letter`; nos volumes deste app (algumas centenas de linhas) o
     * índice não se paga.
     */
    async #assumirODono(dono, anterior) {
        const banco = this.#deps.banco;
        const tabelasDeDados = TABELAS.map((t) => banco.tabela(t));
        // Um array só: a sobrecarga variádica de `transaction` não aceita spread,
        // e são doze tabelas no escopo.
        await banco.transaction('rw', [banco.fila, banco.dead_letter, banco.estado, ...tabelasDeDados], async () => {
            // Chaves das linhas que a purga levou — e só elas saem da fila.
            const purgadas = new Set();
            for (const t of TABELAS) {
                const tabela = banco.tabela(t);
                const deOutraConta = [];
                await tabela.each((linha) => {
                    const dona = linha.user_id;
                    if (dona === CONTA_LOCAL)
                        return;
                    // `typeof` explícito: uma linha sem `user_id` (que o schema não
                    // permite, mas o `each` não garante) fica onde está, em vez de ser
                    // apagada por um `undefined !== dono`.
                    if (typeof dona === 'string' && dona !== dono) {
                        deOutraConta.push(linha.id);
                        purgadas.add(`${t}\0${linha.id}`);
                    }
                });
                if (deOutraConta.length > 0)
                    await tabela.bulkDelete(deOutraConta);
            }
            if (purgadas.size > 0) {
                const orfas = await banco.fila
                    .filter((e) => purgadas.has(`${e.tabela}\0${e.id}`))
                    .primaryKeys();
                if (orfas.length > 0)
                    await banco.fila.bulkDelete(orfas);
            }
            // Perfil e mês derivam a PK do dono. A adoção genérica de `user_id`
            // deixaria a chave da sentinela para trás e criaria conflito no
            // servidor; a unidade específica resolve colisão e fila por LWW.
            await adotarIdentidadesDerivadas(banco, dono);
            for (const t of TABELAS) {
                const tabela = banco.tabela(t);
                const adotadas = [];
                await tabela.each((linha) => {
                    if (linha.user_id === CONTA_LOCAL)
                        adotadas.push({ ...linha, user_id: dono });
                });
                if (adotadas.length > 0)
                    await tabela.bulkPut(adotadas);
            }
            await banco.dead_letter.where('user_id').notEqual(dono).delete();
            if (anterior !== null) {
                for (const t of TABELAS)
                    await banco.estado.delete(`cursor:${t}`);
                // Um marcador antigo da conta que retorna não descreve a base que
                // acabou de ser purgada para recebê-la. O novo pull precisa provar
                // completude outra vez.
                await invalidarBaseDoRelatorio(banco, dono);
            }
            await banco.estado.put({ chave: 'usuario', valor: dono });
        });
    }
    /**
     * O aparelho voltou à rede. No modo manual isso não autoriza sincronizar —
     * manual quer dizer só quando o usuário mandar, e reconectar não é ele
     * mandando (mesmo raciocínio do `visibilitychange`).
     */
    async aoReconectar() {
        if ((await this.getModo()) === 'manual')
            return;
        await this.sincronizarAgora();
    }
    sincronizarAgora() {
        this.#cicloEmCurso ??= this.#ciclo().finally(() => {
            this.#cicloEmCurso = null;
        });
        return this.#cicloEmCurso;
    }
    async relatorioDeMortas() {
        const usuario = await this.#valor('usuario');
        if (usuario === null)
            return [];
        return this.#deps.banco.dead_letter
            .where('user_id')
            .equals(usuario)
            .toArray();
    }
    /**
     * Devolve as linhas mortas à origem certa (depois de o usuário corrigir a
     * causa). As de `push` voltam para a fila; as de `pull` não têm fila para
     * onde voltar — o que as traz de novo é zerar o cursor da tabela delas.
     */
    async reenfileirarMortas() {
        const banco = this.#deps.banco;
        const mortas = await banco.dead_letter.toArray();
        const deEnvio = mortas.filter((m) => m.origem === 'push');
        const cursoresZerados = [
            ...new Set(mortas.filter((m) => m.origem === 'pull').map((m) => m.tabela)),
        ];
        const dono = await this.#valor('usuario');
        await banco.transaction('rw', banco.fila, banco.dead_letter, banco.estado, async () => {
            for (const m of deEnvio) {
                await banco.fila.add({
                    tabela: m.tabela,
                    id: m.id,
                    payload: m.payload,
                    em: m.em,
                });
            }
            for (const t of cursoresZerados)
                await banco.estado.delete(`cursor:${t}`);
            if (dono !== null &&
                cursoresZerados.some(tabelaPertenceABaseDoRelatorio))
                await invalidarBaseDoRelatorio(banco, dono);
            await banco.dead_letter.clear();
        });
        await this.#atualizarContadores();
        return { reenfileiradas: deEnvio.length, cursoresZerados };
    }
    async #atualizarContadores() {
        this.#mudar({
            pendentes: await this.#deps.banco.fila.count(),
            mortas: await this.#deps.banco.dead_letter.count(),
        });
    }
    async #ciclo() {
        // Sem rede não há ciclo: push e pull falhariam por transporte, uma vez
        // por tabela, e a tela mostraria doze "Failed to fetch" vermelhos. Quem
        // retoma é o `aoReconectar()`, no evento `online` do aparelho.
        // A conta vem antes da rede: `usuarioAtual()` lê a sessão local, e sem
        // conta a fase certa é "sem conta", esteja o aparelho conectado ou não.
        const usuario = await this.#deps.transporte.usuarioAtual();
        if (usuario === null) {
            this.#mudar({ fase: 'desligado', erros: [] });
            return this.#estado;
        }
        if (!this.#online()) {
            // Os contadores continuam valendo offline: a fila cresce com as
            // mutações locais e Ajustes tem de mostrar o número certo de pendentes.
            await this.#atualizarContadores();
            this.#mudar({ fase: 'semRede', erros: [] });
            return this.#estado;
        }
        this.#usuarioDoCiclo = usuario;
        this.#mudar({
            fase: 'sincronizando',
            erros: [],
            rejeitadasNoUltimoCiclo: 0,
        });
        try {
            // Conta mudou? Quem assume adota as linhas escritas sem conta, apaga
            // as de qualquer outra conta (com a fila delas — subi-la agora bateria
            // na RLS da conta nova —, as mortas e os cursores da anterior) e grava
            // o dono novo, tudo numa transação. Ver `#assumirODono`. Sem dono
            // anterior não há cursor de outra conta para zerar.
            const anterior = await this.#valor('usuario');
            if (anterior !== usuario)
                await this.#assumirODono(usuario, anterior);
            // Atividade que evita a pausa do Free; falhar aqui não é falha de sync.
            await this.#deps.transporte.heartbeat().catch(() => undefined);
            const falhasDoPush = await this.#push();
            // O marcador descreve o último pull completo, não uma esperança de
            // que o próximo termine. Apagá-lo antes conserva o estado incompleto
            // quando a rede cai no meio; o ramo offline acima nem chega aqui e,
            // por isso, uma base já completa continua utilizável sem rede.
            await invalidarBaseDoRelatorio(this.#deps.banco, usuario);
            const falhasDoPull = await this.#pull();
            const falhouBaseDoRelatorio = falhasDoPull.some((falha) => falha.tabela === 'motor' ||
                falha.tabela === 'realtime' ||
                tabelaPertenceABaseDoRelatorio(falha.tabela));
            if (!falhouBaseDoRelatorio &&
                !(await haLeituraMortaDoRelatorio(this.#deps.banco, usuario)))
                await marcarBaseDoRelatorioCompleta(this.#deps.banco, usuario);
            const falhas = [...falhasDoPush, ...falhasDoPull];
            await this.#atualizarContadores();
            if (falhas.length === 0) {
                this.#backoffMs = 1000;
                const agora = this.#agora();
                await this.#gravarValor('ultimaSincronizacao', agora);
                this.#mudar({
                    fase: 'ocioso',
                    ultimaSincronizacaoEm: agora,
                    erros: [],
                });
            }
            else if (!this.#online()) {
                // A rede caiu no meio do ciclo: as falhas são de transporte, não do
                // app. Mesmo desfecho de quem já começou offline.
                this.#mudar({ fase: 'semRede', erros: [] });
            }
            else {
                this.#mudar({
                    fase: 'erro',
                    erros: falhas,
                });
                if (falhas.some((f) => f.transitoria))
                    this.#agendarNovaTentativa(esperaPedida(falhas));
            }
        }
        catch (causa) {
            // Os contadores expostos não podem congelar num erro duro (ex.: um
            // push/pull que lançou fora dos caminhos de falha esperados).
            try {
                await this.#atualizarContadores();
            }
            catch {
                // Se nem os contadores saem, segue só com o erro original.
            }
            if (!this.#online()) {
                this.#mudar({ fase: 'semRede', erros: [] });
                return this.#estado;
            }
            this.#mudar({
                fase: 'erro',
                erros: [
                    {
                        tabela: 'motor',
                        mensagem: causa instanceof Error ? causa.message : String(causa),
                        transitoria: false,
                    },
                ],
            });
        }
        return this.#estado;
    }
    /**
     * Agenda a próxima tentativa.
     *
     * `esperaPedida` é o `Retry-After` que o servidor mandou (um 429 do
     * Supabase diz em quanto tempo a cota volta). Quando ele diz, obedecer é
     * melhor dos dois lados: voltar antes é bater na porta fechada e gastar
     * outra tentativa da cota; voltar muito depois é deixar o usuário sem
     * sincronizar por mais tempo do que o servidor pediu. O backoff cego só
     * adivinha, e continua sendo o que vale quando ninguém disse nada.
     *
     * Com um pedido do servidor, o backoff cego **não dobra**: a próxima
     * falha sem `Retry-After` recomeça de onde estava, porque um 429 honrado
     * não é sinal de que a rede piorou. O teto é o mesmo dos dois lados —
     * `Retry-After` é cabeçalho de terceiro, e um valor absurdo (ou uma data
     * mal formada que vire um número enorme) não pode estacionar a
     * sincronização para sempre.
     *
     * E há **piso**, não só teto: o backoff cego vale como mínimo. Obedecer a
     * um pedido de espera nunca pode significar esperar **menos** do que já se
     * esperaria sem pedido nenhum — sem o piso, uma rede que já tivesse
     * empurrado o backoff a minutos voltaria a um `Retry-After: 1` e bateria
     * de segundo em segundo justamente no serviço que acabou de dizer
     * "devagar".
     *
     * O que o piso **não** cobre, e vale dizer para não parecer garantia: um
     * servidor que responda `Retry-After: 1` desde a primeira falha mantém o
     * motor nesse ritmo, porque o backoff cego fica em 1 s enquanto o
     * cabeçalho vier (por decisão: um 429 obedecido não é sinal de que a rede
     * piorou) e a desistência é por tempo decorrido. É o ritmo que o próprio
     * servidor pediu, repetidamente; o resíduo está em `docs/pendencias.md`.
     */
    #agendarNovaTentativa(esperaPedida = null) {
        if (this.#novaTentativa !== null)
            clearTimeout(this.#novaTentativa);
        const espera = esperaPedida === null
            ? this.#backoffMs
            : Math.min(Math.max(esperaPedida, this.#backoffMs), TETO_DO_BACKOFF);
        this.#novaTentativa = setTimeout(() => {
            this.#novaTentativa = null;
            void this.sincronizarAgora();
        }, espera);
        if (esperaPedida === null)
            this.#backoffMs = Math.min(this.#backoffMs * 2, TETO_DO_BACKOFF);
    }
    // Push: coleção por coleção, e a falha de uma não derruba as outras.
    async #push() {
        const banco = this.#deps.banco;
        const falhas = [];
        const emFalta = new Set();
        let rejeitadas = 0;
        let volta = 0;
        for (; volta < 20; volta += 1) {
            // O filtro entra ANTES do limit: filtrar depois deixaria uma tabela
            // em falta ocupar a janela inteira e nada mais subiria neste ciclo.
            const entradas = await banco.fila
                .orderBy('seq')
                .filter((e) => !emFalta.has(e.tabela))
                .limit(500)
                .toArray();
            if (entradas.length === 0)
                break;
            const { finais } = dedupFila(entradas);
            const porTabela = new Map();
            for (const e of finais)
                porTabela.set(e.tabela, [...(porTabela.get(e.tabela) ?? []), e]);
            for (const [tabela, grupo] of porTabela) {
                const resultado = await this.#enviarGrupo(tabela, grupo);
                if (resultado.transitoria) {
                    const desistiram = await this.#registrarFalha(tabela, grupo, resultado);
                    falhas.push({
                        tabela,
                        mensagem: desistiram === 0
                            ? `Envio de ${tabela} falhou: ${resultado.mensagem}`
                            : `Envio de ${tabela} falhou: ${resultado.mensagem} — ${String(desistiram)} linha(s) desistiram e estão em "não subiram".`,
                        transitoria: true,
                        ...(resultado.esperarMs === undefined
                            ? {}
                            : { esperarMs: resultado.esperarMs }),
                    });
                    emFalta.add(tabela);
                    continue;
                }
                rejeitadas += resultado.rejeitadas;
                // Confirmação por coleção: tudo desta tabela nesta volta sai da fila
                // (as superadas pela dedup também; as mortas já foram movidas).
                const seqs = entradas
                    .filter((e) => e.tabela === tabela)
                    .map((e) => e.seq);
                await banco.fila.bulkDelete(seqs);
            }
        }
        // O laço saiu por esgotar as 20 voltas, não por esvaziar a fila: em
        // silêncio isso pareceria sucesso enquanto a fila real continua cheia.
        if (volta === 20 && (await banco.fila.count()) > 0) {
            falhas.push({
                tabela: 'motor',
                mensagem: 'Fila muito grande; o próximo ciclo continua.',
                transitoria: false,
            });
        }
        if (rejeitadas > 0)
            await this.#gravarValor('rejeitadas', `${this.#agora()} ${String(rejeitadas)}`);
        this.#mudar({ rejeitadasNoUltimoCiclo: rejeitadas });
        return falhas;
    }
    /**
     * Anota a falha em cada entrada que ainda não subiu, e manda para
     * `dead_letter` a que já não tem mais o que esperar.
     *
     * Por que desistir existe: falha transitória mantinha a fila e repetia com
     * backoff **para sempre**. Uma linha que o servidor nunca vai aceitar (um
     * 403 de RLS que não cede) travava a tabela inteira, e junto dela as
     * linhas boas escritas depois — e como nada ia para `dead_letter`,
     * `mortas` ficava em zero e a tela de Sincronização não tinha o que
     * mostrar nem o que reenfileirar. Desistir não descarta nada:
     * `dead_letter` é a lista visível, e `reenfileirarMortas()` a devolve
     * inteira à fila.
     *
     * Por que são dois critérios: `TETO_DE_TENTATIVAS` e `JANELA_DE_FALHA_MS`
     * explicam. Em resumo, o 403 acusa a linha (contar voltas é o certo) e o
     * resto acusa o transporte (contar voltas drenaria a fila em dois minutos
     * de rede caída; o certo é contar tempo).
     *
     * O grupo é o deduplicado; ao desistir de uma linha, todas as entradas da
     * fila daquela linha saem juntas, porque as superadas pela dedup são
     * versões velhas da mesma coisa e não têm para onde ir sozinhas.
     */
    async #registrarFalha(tabela, grupo, resultado) {
        const banco = this.#deps.banco;
        const dono = this.#donoDoCiclo();
        const em_erro = this.#agora();
        const motivo = resultado.mensagem;
        const jaAceitas = new Set(resultado.enviadas.map((e) => e.seq));
        let desistiram = 0;
        await banco.transaction('rw', banco.fila, banco.dead_letter, async () => {
            const desistir = async (entrada, porque) => {
                await banco.dead_letter.add({
                    ...entrada,
                    origem: 'push',
                    motivo: `${porque}: ${motivo}`,
                    em_erro,
                    user_id: dono,
                });
                const irmas = await banco.fila
                    .where('id')
                    .equals(entrada.id)
                    .filter((o) => o.tabela === tabela)
                    .toArray();
                await banco.fila.bulkDelete(irmas.map((o) => o.seq));
                desistiram += 1;
            };
            // Lote que o servidor aceitou antes de a falha aparecer: essas linhas
            // subiram. Não levam a falha das que vieram depois, e o que estivesse
            // carimbado nelas se apaga — a sequência de falhas foi interrompida.
            for (const enviada of resultado.enviadas) {
                if (enviada.tentativas === undefined &&
                    enviada.primeiraFalhaEm === undefined)
                    continue;
                await banco.fila.put({
                    seq: enviada.seq,
                    tabela: enviada.tabela,
                    id: enviada.id,
                    payload: enviada.payload,
                    em: enviada.em,
                });
            }
            for (const entrada of grupo) {
                if (jaAceitas.has(entrada.seq))
                    continue;
                // A bissecção de 4xx já pode ter mandado esta linha para o
                // `dead_letter` e apagado a entrada; reusar o `seq` num `add` daria
                // `ConstraintError` e abortaria a transação inteira, levando junto as
                // linhas que só precisavam de mais uma volta.
                if ((await banco.fila.get(entrada.seq)) === undefined)
                    continue;
                if (resultado.recusa) {
                    const tentativas = (entrada.tentativas ?? 0) + 1;
                    if (tentativas < TETO_DE_TENTATIVAS) {
                        await banco.fila.update(entrada.seq, { tentativas });
                        continue;
                    }
                    await desistir({ ...entrada, tentativas }, `desistência após ${String(tentativas)} tentativas`);
                    continue;
                }
                // Transporte: o que conta é há quanto tempo esta linha não consegue
                // subir, e não quantas voltas o app deu nesse meio-tempo. O que se
                // mede é relógio de parede desde a primeira falha, não falha
                // ininterrupta: o app fechado por trinta horas e uma falha ao voltar
                // bastam. O lado inseguro do erro (relógio para trás) mantém a linha
                // na fila, que é o lado certo.
                const desde = entrada.primeiraFalhaEm ?? em_erro;
                const decorrido = new Date(em_erro).getTime() - new Date(desde).getTime();
                if (decorrido < JANELA_DE_FALHA_MS) {
                    if (entrada.primeiraFalhaEm === undefined)
                        await banco.fila.update(entrada.seq, { primeiraFalhaEm: desde });
                    continue;
                }
                await desistir(entrada, `desistência após 24 h desde a primeira falha (${desde})`);
            }
        });
        return desistiram;
    }
    /** Um grupo em lotes; 4xx bissecta até isolar a linha culpada. */
    async #enviarGrupo(tabela, grupo) {
        let rejeitadas = 0;
        const enviadas = [];
        for (const lote of emLotes(grupo, LOTE_DO_PUSH)) {
            const r = await this.#enviarLote(tabela, lote);
            enviadas.push(...r.enviadas);
            if (r.transitoria)
                return { ...r, enviadas };
            rejeitadas += r.rejeitadas;
        }
        return {
            transitoria: false,
            recusa: false,
            mensagem: '',
            rejeitadas,
            enviadas,
        };
    }
    async #enviarLote(tabela, lote) {
        // O dono do ciclo carimba a linha na saída: ver `paraRemoto`. Sem isso
        // uma linha escrita antes de haver conta chega ao servidor com o dono
        // errado, volta em 403 e trava a fila da tabela para sempre.
        const resposta = await this.#deps.transporte.upsert(tabela, lote.map((e) => paraRemoto(tabela, e.payload, this.#donoDoCiclo())));
        if (resposta.status === 200) {
            const aceitas = new Set(resposta.aceitas.map((l) => String(l['id'])));
            return {
                transitoria: false,
                recusa: false,
                mensagem: '',
                // A rejeitada pelo `lww_guard` também subiu: o servidor respondeu e
                // preferiu a versão dele. O que ela não é, é falha.
                rejeitadas: lote.filter((e) => !aceitas.has(e.id)).length,
                enviadas: [...lote],
            };
        }
        // 403 é o da RLS: o servidor acusa a linha. Continua transitória — pode
        // ser o token ainda sem o `sub` certo, ou uma política que acabou de
        // mudar —, mas é a única falha que conta tentativa.
        if (resposta.status === 500 || resposta.status === 403)
            return {
                transitoria: true,
                recusa: resposta.status === 403,
                mensagem: resposta.erro ?? 'falha de rede',
                rejeitadas: 0,
                enviadas: [],
                ...(resposta.esperarMs === undefined
                    ? {}
                    : { esperarMs: resposta.esperarMs }),
            };
        // 400/409: uma linha do lote é inválida para o servidor. Bissectar.
        if (lote.length === 1) {
            const culpada = lote[0];
            if (culpada === undefined)
                return {
                    transitoria: false,
                    recusa: false,
                    mensagem: '',
                    rejeitadas: 0,
                    enviadas: [],
                };
            await this.#deps.banco.transaction('rw', this.#deps.banco.fila, this.#deps.banco.dead_letter, async () => {
                await this.#deps.banco.dead_letter.add({
                    ...culpada,
                    origem: 'push',
                    motivo: `${String(resposta.status)}: ${resposta.erro ?? ''}`,
                    em_erro: this.#agora(),
                    user_id: this.#donoDoCiclo(),
                });
                await this.#deps.banco.fila.delete(culpada.seq);
            });
            return {
                transitoria: false,
                recusa: false,
                mensagem: '',
                rejeitadas: 0,
                enviadas: [],
            };
        }
        const meio = Math.ceil(lote.length / 2);
        // Uma metade que passou é uma metade enviada, mesmo que a outra falhe:
        // sem carregar `enviadas` pela recursão, as linhas já aceitas levariam a
        // falha das seguintes.
        const esquerda = await this.#enviarLote(tabela, lote.slice(0, meio));
        if (esquerda.transitoria)
            return esquerda;
        const direita = await this.#enviarLote(tabela, lote.slice(meio));
        const enviadas = [...esquerda.enviadas, ...direita.enviadas];
        if (direita.transitoria)
            return { ...direita, enviadas };
        return {
            transitoria: false,
            recusa: false,
            mensagem: '',
            rejeitadas: esquerda.rejeitadas + direita.rejeitadas,
            enviadas,
        };
    }
    async #pull() {
        const banco = this.#deps.banco;
        const falhas = [];
        for (const tabela of TABELAS) {
            const cursor = (await this.#valor(`cursor:${tabela}`)) ?? CURSOR_ZERO;
            // Sobreposição: uma transação que começou antes e cometeu depois pode
            // ter carimbo menor que o cursor. Reaplicar é no-op pelo LWW.
            let desde = new Date(Math.max(0, new Date(cursor).getTime() - SOBREPOSICAO_DO_PULL_MS)).toISOString();
            for (;;) {
                let linhas;
                try {
                    linhas = await this.#deps.transporte.select(tabela, desde, PAGINA_DO_PULL);
                }
                catch (causa) {
                    falhas.push({
                        tabela,
                        mensagem: `Busca de ${tabela} falhou: ${causa instanceof Error ? causa.message : String(causa)}`,
                        transitoria: true,
                    });
                    break;
                }
                if (linhas.length === 0)
                    break;
                let uteis = linhas;
                const cheia = linhas.length === PAGINA_DO_PULL;
                if (cheia) {
                    const ultimo = normalizarIso(linhas[linhas.length - 1]?.['server_updated_at']);
                    uteis = linhas.filter((l) => normalizarIso(l['server_updated_at']) !== ultimo);
                }
                if (uteis.length === 0) {
                    // Um grupo com o mesmo carimbo maior ou igual à página inteira: o
                    // descarte do último grupo apagou tudo e o cursor não tem para onde
                    // avançar. Isso trava o pull silenciosamente se não for reportado.
                    falhas.push({
                        tabela,
                        mensagem: `Grupo de carimbo maior que a página em ${tabela}; cursor não pôde avançar.`,
                        transitoria: false,
                    });
                    break;
                }
                const tabelaLocal = banco.tabela(tabela);
                await banco.transaction('rw', tabelaLocal, banco.dead_letter, async () => {
                    for (const row of uteis) {
                        let remota;
                        try {
                            remota = deRemoto(tabela, row);
                        }
                        catch (causa) {
                            if (causa instanceof ErroDeMapeamento) {
                                // seq é auto-incremento (++seq): nunca gravar 0 literal, ou
                                // a segunda linha inválida da página colide (ConstraintError)
                                // e aborta a transação inteira — o pull nunca mais avança.
                                // E a própria gravação em dead_letter não pode derrubar a
                                // página: se falhar, vira uma falha visível, não um travamento.
                                try {
                                    await banco.dead_letter.add({
                                        tabela,
                                        id: String(row['id']),
                                        payload: row,
                                        em: this.#agora(),
                                        origem: 'pull',
                                        motivo: causa.message,
                                        em_erro: this.#agora(),
                                        user_id: this.#donoDoCiclo(),
                                    });
                                }
                                catch (erroDeGravacao) {
                                    falhas.push({
                                        tabela,
                                        mensagem: `Gravação em dead_letter falhou para ${tabela}: ${erroDeGravacao instanceof Error ? erroDeGravacao.message : String(erroDeGravacao)}`,
                                        transitoria: false,
                                    });
                                }
                                continue;
                            }
                            throw causa;
                        }
                        const local = await tabelaLocal.get(remota.id);
                        if (deveAplicarRemota(local?.updated_at, remota.updated_at))
                            await tabelaLocal.put(remota);
                        else if (local &&
                            local.server_updated_at !== remota.server_updated_at)
                            await tabelaLocal.put({
                                ...local,
                                server_updated_at: remota.server_updated_at,
                            });
                    }
                });
                const maior = uteis.reduce((m, l) => normalizarIso(l['server_updated_at']) > m
                    ? normalizarIso(l['server_updated_at'])
                    : m, cursor);
                await this.#gravarValor(`cursor:${tabela}`, maior);
                if (!cheia)
                    break;
                desde = maior;
            }
        }
        return falhas;
    }
}
/** Lê `Retry-After` de 429 e 503 — os dois códigos em que ele significa
 *  "volte depois", e não "isto está quebrado". */
export function escutarRetryAfter(base = (...args) => globalThis.fetch(...args), agora = () => Date.now()) {
    let terminaEm = null;
    return {
        fetch: async (...args) => {
            const resposta = await base(...args);
            if (resposta.status === 429 || resposta.status === 503) {
                const ms = emMilissegundos(resposta.headers.get('Retry-After'), agora());
                if (ms !== null)
                    terminaEm = agora() + ms;
            }
            return resposta;
        },
        consumir() {
            if (terminaEm === null)
                return null;
            const resta = terminaEm - agora();
            terminaEm = null;
            return resta > 0 ? resta : null;
        },
    };
}
/** As duas formas do `Retry-After`: segundos ("120") e data HTTP ("Wed, 21
 *  Oct 2026 07:28:00 GMT"). Qualquer outra coisa é `null` — cabeçalho de
 *  terceiro que não se entende não vira espera inventada. */
function emMilissegundos(cabecalho, agoraMs) {
    if (cabecalho === null)
        return null;
    const texto = cabecalho.trim();
    if (texto === '')
        return null;
    const segundos = Number(texto);
    if (Number.isFinite(segundos))
        return segundos >= 0 ? Math.round(segundos * 1000) : null;
    const data = Date.parse(texto);
    return Number.isNaN(data) ? null : Math.max(0, data - agoraMs);
}
/** O transporte real, sobre o supabase-js. */
export function transporteSupabase(cliente, escuta) {
    return {
        async upsert(tabela, linhas) {
            const { data, error, status } = await cliente
                .from(tabela)
                // Sem `as never[]`: a linha local tem exatamente o shape do `Insert`
                // do esquema (`paraRemoto` só tira `server_updated_at`). O que ainda
                // exige asserção é que o payload da fila é `Record<string, unknown>`
                // e `tabela` é a união das oito, então o TypeScript não consegue
                // casar linha e tabela sozinho — a asserção nomeia o tipo certo.
                .upsert(linhas, {
                onConflict: 'id',
                // `Prefer: missing=default`, e é preciso dizer com precisão o
                // que ele faz — porque não é o que parece.
                //
                // O PostgREST monta a lista de colunas do INSERT a partir da
                // **união das chaves do corpo**. Uma coluna que este cliente não
                // conhece (esquema novo, cliente velho) foi descartada pelo zod
                // e não está em linha nenhuma do lote: ela simplesmente não
                // entra na lista de colunas. Logo não é inserida como NULL — o
                // Postgres lhe dá o default da tabela — nem entra no `ON
                // CONFLICT DO UPDATE SET`, então sobrevive intacta ao
                // ida-e-volta. **Isso vale com ou sem este cabeçalho.**
                //
                // O que o cabeçalho muda é só o lote **heterogêneo**: quando uma
                // chave está em algumas linhas e falta em outras, sem ele as
                // linhas que a omitem levam NULL; com ele, o default da coluna.
                // Este cliente nunca produz lote assim — as linhas de um upsert
                // saem todas do mesmo schema zod, com o mesmo conjunto de
                // chaves, e é por isso que `postgrestEmMemoria` pode recusar o
                // caso heterogêneo sem nunca atrapalhar o cliente.
                //
                // Fica, então, como seguro barato: no dia em que um caminho novo
                // montar um lote de chaves desiguais, o esquema quase todo
                // `not null default ''` / `default 0` / `default false` daria
                // 23502, o motor bissectaria o lote até a linha e uma linha boa
                // acabaria em `dead_letter`. Um cabeçalho evita isso sem custo.
                defaultToNull: false,
            })
                .select('id, updated_at');
            if (!error)
                return {
                    status: 200,
                    aceitas: data ?? [],
                };
            // Auth expirada e limite de taxa não são erros da linha: drená-las para
            // dead_letter seria descartar dados bons por causa do token ou do 429.
            // Tratamos como transitórias (500), como as 5xx.
            const TRANSITORIAS_4XX = new Set([401, 408, 429]);
            const codigo = status === 409 || error.code === '23505'
                ? 409
                : status === 403
                    ? // O 403 da RLS sobe como 403, e não disfarçado de 500: o motor
                        // o trata como transitória do mesmo jeito, mas o número que
                        // chega nele é o mesmo que o dublê devolve — sem isso a suíte
                        // provaria um caminho que o servidor real não toma.
                        403
                    : TRANSITORIAS_4XX.has(status)
                        ? 500
                        : status >= 400 && status < 500
                            ? 400
                            : 500;
            const esperarMs = escuta?.consumir() ?? null;
            return {
                status: codigo,
                aceitas: [],
                erro: error.message,
                ...(esperarMs === null ? {} : { esperarMs }),
            };
        },
        async select(tabela, desde, limite) {
            const { data, error } = await cliente
                .from(tabela)
                .select('*')
                .gte('server_updated_at', desde)
                .order('server_updated_at', { ascending: true })
                .order('id', { ascending: true })
                .limit(limite);
            if (error)
                throw new Error(error.message);
            return data ?? [];
        },
        async heartbeat() {
            await cliente.from('heartbeat').select('id').limit(1);
        },
        async usuarioAtual() {
            const { data } = await cliente.auth.getSession();
            return data.session?.user.id ?? null;
        },
    };
}
//# sourceMappingURL=motor.js.map